import { ForwardedRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAdd } from '@components/icons';
import classNames from 'classnames';

export const ActionButton = ({
  label,
  customClasses,
  onClick,
}: {
  label?: string;
  customClasses?: string;
  onClick?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <button
      tabIndex={0}
      className={classNames(
        'flex-col justify-center items-center shrink fill-dark',
        {
          'pt-2 pb-1.5 pl-2 pr-4 rounded-sm': label && !customClasses,
          'p-3 rounded-xl': !label && !customClasses,
        },
        customClasses
      )}
      onClick={onClick}>
      <span className='justify-center items-center flex relative gap-1'>
        <IconAdd className={classNames('w-6 h-6 text-negative')} />
        {label && <span className='text-center text-negative text-action-button-s'>{t(label)}</span>}
      </span>
    </button>
  );
};

export const ActionItemButton = forwardRef(
  (
    {
      onClick,
      id,
      text,
      Icon,
      active,
      disabled,
      customTextColor,
      dataTestId,
    }: {
      id?: string;
      onClick: (e?: React.MouseEvent<HTMLDivElement>) => void;
      text: string;
      Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
      active: boolean;
      disabled?: boolean;
      customTextColor?: string;
      dataTestId?: string;
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => (
    <div
      id={id}
      ref={ref}
      role='button'
      tabIndex={0}
      data-testid={dataTestId}
      className={classNames(
        'flex items-center rounded-lg px-6 xs:px-4 py-4 xs:py-2 text-headline-xs xs:text-subtitle-m w-full gap-3',
        {
          'text-primary': !customTextColor,
          'fill-secondary': !active,
          'fill-secondary-active': active,
          'cursor-pointer-none pointer-events-none fill-secondary-disabled text-transparent-20': disabled,
        },
        customTextColor
      )}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick}>
      {Icon && <Icon className='w-6 h-6 text-primary text-center' />}
      {text}
    </div>
  )
);
